/**
 * Kiosk product catalog.
 *
 * Two catalogs:
 *  - INGREDIENTS (S1–S7) — the base liquids on the physical valves; these are
 *    what the Custom Mix screen lets the customer combine.
 *  - MAIN_MENU — preset drinks; each is a 200ml recipe (ml per ingredient)
 *    that the machine dispenses as a multi-valve CUSTOM pour.
 *
 * Halal vs full list is selected by REACT_APP_HALAL_MODE (overridable at runtime
 * from the admin Settings page — see `settings.ts`).
 *
 * NB. `iced-tea` is the UI-facing ingredient — the slider value the customer
 * sees and adjusts. The actual liquid in the S2 valve is pure iced-tea
 * concentrate; one customer-facing 'iced-tea' ml is split 1:5 into pure
 * concentrate + water at pour time (see `ICED_TEA_PURE_RATIO` in `protocol.ts`).
 */
import { getBool } from './settings';

export const HALAL_MODE = getBool('halalMode');

export type ProductDef = {
  id: string;
  icon: string;
  basePrice: number;
};

/** Per-ingredient ml map — keys are the S1–S7 ingredient ids (UI-facing). */
export type CustomMix = {
  'butterfly-pea-flower-tea': number;
  /** UI-facing iced-tea total ml (split 1:5 into pure concentrate + water at pour time). */
  'iced-tea': number;
  water: number;
  lime: number;
  'iced-coffee': number;
  /** Sour cherry juice (Vişne Suyu). */
  'vishne-juice': number;
  lemonade: number;
};

/** A Main Menu product is a preset recipe over the S1–S7 ingredients. */
export type MainMenuProductDef = ProductDef & { customMix: CustomMix };

/** All-zero ingredient map — the starting point for the Custom Mix screen. */
export const customMix: CustomMix = {
  'butterfly-pea-flower-tea': 0,
  'iced-tea': 0,
  water: 0,
  lime: 0,
  'iced-coffee': 0,
  'vishne-juice': 0,
  lemonade: 0,
};

/** Build a recipe from the zero map, setting only the named ingredients. */
const recipe = (parts: Partial<CustomMix>): CustomMix => ({ ...customMix, ...parts });

// ── Ingredients on valves S1…S7 — the Custom Mix screen ──────────────────────
const INGREDIENTS: ProductDef[] = [
  { id: 'butterfly-pea-flower-tea', icon: '🫖', basePrice: 3.5 },                                                  // S1
  { id: 'iced-tea',     icon: '🧋', basePrice: 2.5 },                                                              // S2 (UI label; physical valve holds pure concentrate)
  { id: 'water',        icon: 'https://img.icons8.com/?size=96&id=vqwXLygaIdwE&format=png', basePrice: 1.5 },      // S3
  { id: 'vishne-juice', icon: '🍒', basePrice: 3.0 },                                                              // S4 (Sour Cherry Juice / Vişne Suyu)
  { id: 'iced-coffee',  icon: 'https://img.icons8.com/?size=96&id=7hxTNGKKQMYI&format=png', basePrice: 3.5 },      // S5
  { id: 'lime',         icon: '🍋‍🟩', basePrice: 3.0 },                                                       // S6 (green lemon / Misket Limonu)
  { id: 'lemonade',     icon: '🍋', basePrice: 2.5 },                                                              // S7 (yellow lemon)
];

// ── Main Menu presets — each recipe totals 200ml ─────────────────────────────
const MAIN_MENU: MainMenuProductDef[] = [
  { id: 'rush-cocktail', icon: '🧊', basePrice: 3.0,
    customMix: recipe({ 'butterfly-pea-flower-tea': 100, water: 50, lime: 50 }) },
  { id: 'cool-lime', icon: '🍋‍🟩', basePrice: 3.5,
    customMix: recipe({ water: 150, lime: 50 }) },
  // Iced tea preset: 200ml UI iced-tea → pours as ~33ml pure concentrate + ~167ml water.
  { id: 'iced-tea', icon: '🧋', basePrice: 3.5,
    customMix: recipe({ 'iced-tea': 200 }) },
  { id: 'iced-coffee', icon: 'https://img.icons8.com/?size=96&id=7hxTNGKKQMYI&format=png', basePrice: 3.5,
    customMix: recipe({ 'iced-coffee': 200 }) },
  { id: 'vishne-juice', icon: '🍒', basePrice: 2.5,
    customMix: recipe({ 'vishne-juice': 200 }) },
  { id: 'lemonade', icon: '🍋', basePrice: 2.5,
    customMix: recipe({ lemonade: 200 }) },
  { id: 'water', icon: 'https://img.icons8.com/?size=96&id=vqwXLygaIdwE&format=png', basePrice: 1.5,
    customMix: recipe({ water: 200 }) },
];

/** Standard + halal catalogs (currently identical — all drinks non-alcoholic). */
export const PRODUCT_DEFS_FULL: ProductDef[] = INGREDIENTS;
export const PRODUCT_DEFS_HALAL: ProductDef[] = INGREDIENTS;
export const PRODUCT_DEFS_MAIN_MENU_FULL: MainMenuProductDef[] = MAIN_MENU;
export const PRODUCT_DEFS_MAIN_MENU_HALAL: MainMenuProductDef[] = MAIN_MENU;

/** Active lists — switch automatically based on halal mode. */
export const PRODUCT_DEFS: ProductDef[] = HALAL_MODE ? PRODUCT_DEFS_HALAL : PRODUCT_DEFS_FULL;
export const MAIN_MENU_PRODUCT_DEFS: MainMenuProductDef[] =
  HALAL_MODE ? PRODUCT_DEFS_MAIN_MENU_HALAL : PRODUCT_DEFS_MAIN_MENU_FULL;
